// src/employee/context/EmployeeDataContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";

const EmployeeDataContext = createContext();

export const EmployeeDataProvider = ({ children }) => {
  const { user } = useEmployeeAuth();
  const [data, setData] = useState({
    tasks: [],
    leaves: [],
    timesheets: [],
    payouts: [],
    notifications: [],
    employee: null,
    loading: true
  });

  useEffect(() => {
    if (!user?.email) {
      setData(prev => ({ ...prev, loading: false }));
      return;
    }

    const unsubs = [];

    // Tasks
    const q1 = query(collection(db, "tasks"), where("employeeEmails", "array-contains", user.email));
    unsubs.push(onSnapshot(q1, snap => {
      setData(prev => ({ ...prev, tasks: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
    }));

    // Leaves
    //    const q2 = query(collection(db, "leaves"), where("employeeEmail", "==", user.email));
    let q2;

    if (user.role === "hr") {
      // HR → get ALL leaves
      q2 = query(collection(db, "leaves"));
    } else {
      // Employee → only their leaves
      q2 = query(
        collection(db, "leaves"),
        where("employeeEmail", "==", user.email)
      );
    }
    unsubs.push(onSnapshot(q2, snap => {
      setData(prev => ({ ...prev, leaves: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
    }));

    // Timesheets
    const q3 = query(collection(db, "timesheets"), where("employeeEmail", "==", user.email));
    unsubs.push(onSnapshot(q3, snap => {
      setData(prev => ({ ...prev, timesheets: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
    }));

    // Payouts (CRITICAL: use finalPaid, not finalAmount)
    let q4;
    if (user.role === "hr") {
      q4 = query(collection(db, "payouts"));
    } else {
      q4 = query(collection(db, "payouts"), where("employeeEmail", "==", user.email));
    }
    unsubs.push(onSnapshot(q4, snap => {
      setData(prev => ({ ...prev, payouts: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
    }));

    // Notifications
    const q5 = query(
      collection(db, "employeeNotifications"),
      where("employeeEmail", "==", user.email),
      orderBy("createdAt", "desc")
    );
    unsubs.push(onSnapshot(q5, snap => {
      setData(prev => ({ ...prev, notifications: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
    }));

    // ADD THIS: Fetch current employee's full profile (including salaryStructure)
        // FIXED: CORRECTLY FETCH EMPLOYEE PROFILE WITH salaryStructure
        const employeeQuery = query(
          collection(db, "employees"),
          where("email", "==", user.email)
        );

        unsubs.push(
          onSnapshot(employeeQuery, (snap) => {
            if (snap.empty) {
              console.log("No employee document found for:", user.email);
              setData(prev => ({ ...prev, employee: null }));
              return;
            }

            const empDoc = snap.docs[0];
            const empData = empDoc.data();


            setData(prev => ({
              ...prev,
              employee: {
                id: empDoc.id,                    // ← THIS IS THE EMAIL (correct)
                email: empDoc.id,                 // ← same as id
                ...empData,
                salaryStructure: empData.salaryStructure || {} // ← ensure it exists
              }
            }));
          }, (error) => {
            console.error("Error loading employee profile:", error);
          })
        );
    setData(prev => ({ ...prev, loading: false }));

    return () => unsubs.forEach(u => u());
  }, [user?.email]);

  return (
    <EmployeeDataContext.Provider value={{ ...data }}>
      {children}
    </EmployeeDataContext.Provider>
  );
};

export const useEmployeeData = () => {
  const context = useContext(EmployeeDataContext);
  if (!context) throw new Error("useEmployeeData must be used within EmployeeDataProvider");
  return context;
};